// Eskom PARCC — typed WRITE data layer over Firebase Data Connect.
//
// The companion to the READ layer (src/lib/db.ts). This module is the
// application's single source of *writes*: it maps clean, typed input into the
// EXACT Variables objects the generated mutations accept, then calls those
// mutations against the shared `dc` instance.
//
// Two layers, deliberately separated for testability:
//   1. PURE variable mappers (`transactionInsertVars`, `transactionUpdateVars`,
//      `workingPaperInsertVars`/`…UpdateVars`, `cesRowVars`, `findingInsertVars`,
//      `findingUpdateVars`) — no Firebase, unit-tested directly. Each is
//      field-locked to the columns its mutation accepts (see
//      src/data/generated/index.d.ts), normalises emails, and coerces numerics.
//   2. Firebase-bound WRAPPERS (`upsertTransaction`, `upsertWorkingPaperForTx`,
//      `replaceCesRowsForTx`, `replaceFindingsForTx`, `updateOneFinding`) — they
//      resolve the REAL transaction UUID + (where needed) working-paper UUID via
//      the read layer, never trusting the read placeholders, then sequence the
//      mutations.
//
// Concurrency: see src/lib/concurrency.ts. Detection is CLIENT-SIDE only — the
// current UpdateTransactionData mutation updates by id without an atomic
// rowVersion compare-and-set, so a guard narrows but cannot eliminate a
// last-write-wins race. True atomic CAS needs a connector change (a later task).
import {
  insertTransactionData,
  updateTransactionData,
  reviveTransaction,
  reviveTransactionForImport,
  detachRevivedFindings,
  transactionIdByRef,
  softDeleteTransaction as softDeleteTransactionMutation,
  userByEmail,
  insertWorkingPaper,
  updateWorkingPaperByTx,
  workingPaperByTx,
  deleteCesRowsForTx,
  insertCesRow,
  softDeleteFindingsForTx,
  insertFindingRow,
  updateFindingRow,
  deleteFinding,
  findingsForTx,
  insertEvaluationSheetBackup as insertEvaluationSheetBackupMutation,
  insertAuditLog as insertAuditLogMutation,
  insertReportRun as insertReportRunMutation,
  insertRfiRegister as insertRfiRegisterMutation,
  clearRfiRegister as clearRfiRegisterMutation,
  upsertRtmSignoff as upsertRtmSignoffMutation,
  upsertReportNarrative as upsertReportNarrativeMutation,
  deleteReportNarrative as deleteReportNarrativeMutation,
  upsertRtmStatus as upsertRtmStatusMutation,
  markNotificationRead as markNotificationReadMutation,
  markAllNotificationsRead as markAllNotificationsReadMutation,
  type InsertRfiRegisterVariables,
  type UpsertRtmSignoffVariables,
  type InsertTransactionDataVariables,
  type UpdateTransactionDataVariables,
  type InsertWorkingPaperVariables,
  type UpdateWorkingPaperVariables,
  type UpdateWorkingPaperByTxVariables,
  type InsertCesRowVariables,
  type InsertFindingRowVariables,
  type UpdateFindingRowVariables,
  type InsertEvaluationSheetBackupVariables,
  type InsertAuditLogVariables,
  type InsertReportRunVariables,
} from '@eskom-parcc/dataconnect';
import { dc } from './firebase';
import { retryUntilFound } from './retry';
import { normalizeEmail } from './email';
import { assertWritable } from './readOnly';
import { IMPORT_UPDATE_COLUMNS, BULK_UPDATE_COLUMNS } from './importColumns';
export { IMPORT_UPDATE_COLUMNS, BULK_UPDATE_COLUMNS } from './importColumns';
import { normalizeFindingStatus } from '../domain/normalize';
import { resolveTransactionRef } from './db';
import { planFindingReconcile, type ReconcileClosedVia, type ReconcileExisting } from './findingReconcile';
import type { RuleReferences } from '../domain/rulebook/ruleReferences';
import type { FieldChange } from './fieldDiff';

/**
 * Caller-supplied audit detail for a transaction write: the changed-field labels
 * for the note ("Period, Compliance level") and the from/to `changes` for the
 * payload. Computed CALLER-SIDE — where the old row and the new input are both in
 * hand — never by re-reading the old row here (TransactionByRef returns only
 * id/rowVersion/deleted, and widening it would add a read per row on import).
 */
export interface AuditContext {
  note?: string;
  changes?: FieldChange[];
}

// --- Clean typed inputs ----------------------------------------------------
// The write surfaces speak these shapes; the pure mappers turn them into the
// generated Variables objects. Inputs are intentionally narrower than the domain
// types — only the columns each mutation can write.

/** Fields writable on a transaction INSERT (referenceNo is the required key). */
export interface TransactionInsertInput {
  referenceNo: string;
  baseNo?: string;
  recordLabel?: string;
  dataset?: string;
  year?: string;
  period?: string;
  division?: string;
  supplier?: string;
  csdNumber?: string;
  value?: number;
  valueDisplay?: string;
  mechanism?: string;
  /** The persisted boolean review flag (a writable column on both insert and update). */
  reviewed?: boolean;
  /** ISO timestamp stamped when a review is completed (owner feedback #8, Aug 2026). */
  reviewedAt?: string;
  /** ISO timestamp stamped when the transaction's working paper is captured. */
  capturedDate?: string;
  gapsStatus?: string;
  complianceLevel?: string;
  complianceLevelOverride?: string;
  selfDeclaration?: string;
  initialFindings?: string;
  updatedFindings?: string;
  findingText?: string;
  link?: string;
  reviewStatus?: string;
  progress?: string;
  workingPaperNotes?: string;
  assignedToEmail?: string;
  assignedByEmail?: string;
  reviewerEmail?: string;
  // Owner feedback points 3 + 4 (additive population/report columns):
  onDashboard?: boolean;
  panel?: boolean;
  assignedAt?: string;
  ou?: string;
  contractNumber?: string;
  cirNumber?: string;
  linkProvided?: boolean;
  linkIssue?: string;
  // Feedback R3 #1 — the working paper's grey header fields (all six writable on
  // insert AND update; the reviewer-entered four are captured on the CES page).
  bu?: string;
  briefDescription?: string;
  buyer?: string;
  prNumber?: string;
  procurementManager?: string;
  endUser?: string;
}

/**
 * Fields writable on a transaction UPDATE. Mirrors UpdateTransactionDataVariables
 * minus `id` (supplied separately). NB: `referenceNo` is NOT an update column —
 * a reference is immutable once assigned, and the mutation does not accept it.
 * `reviewed` (carried through from {@link TransactionInsertInput}) IS an update
 * column — see `transactionUpdateVars`.
 */
// NB: csdNumber IS an update column since R3 #1 — it is one of the four
// reviewer-entered OpenText header fields. `period` IS one since Aug 2026 (owner
// feedback: operators must be able to correct a transaction's period after
// creation, via Manage and the bulk update).
export type TransactionUpdateInput = Omit<
  TransactionInsertInput,
  'referenceNo' | 'baseNo' | 'recordLabel' | 'year'
>;

/** A working-paper input — same shape for insert and update (id/transactionId added by the wrapper). */
export interface WorkingPaperInput {
  progress?: string;
  complianceLevel?: string;
  findingText?: string;
  workingPaperNotes?: string;
  reviewStatus?: string;
  reviewerEmail?: string;
  /** Free-text name of the ACTUAL reviewer (owner feedback #8: coordinators
   * capture on behalf of reviewers, so the reviewer's name is recorded here). */
  reviewerName?: string;
  /** ISO timestamp stamped when the paper's review is completed. */
  reviewedAt?: string;
  /** ISO timestamp stamped when the paper is captured/saved. */
  capturedDate?: string;
  assignedToEmail?: string;
  assignedByEmail?: string;
  capturedByEmail?: string;
}

/** A single CES row to write (ids are supplied by the wrapper after resolution). */
export interface CesRowInput {
  seq: number;
  stage: string;
  criterion: string;
  status?: string;
  finding?: string;
}

/** A finding to insert (transactionId + referenceNo supplied by the wrapper). */
export interface FindingInsertInput {
  text: string;
  source?: string;
  legacyStatus?: string;
  anchors?: string[];
  suggestedCompliance?: string;
  finalCompliance?: string;
  rationale?: string;
  confidence?: string;
  route?: string;
  matchedRules?: string[];
  effectiveStatus?: string;
  reviewNotes?: string;
  /** Regulatory references for the matched rule; serialised to JSON (item 1). */
  regulatoryRefs?: RuleReferences;
  /** The working-paper procedure the finding was captured against (R3 #2). */
  criterion?: string;
  /** AI provenance — set only when a reviewer confirmed an engine proposal. */
  aiPfmaSection?: string;
  /** JSON array of { doc, page, quote } the engine's verdict cited. */
  aiCitations?: string;
  aiModel?: string;
  aiRunId?: string;
  seeded?: boolean;
  /** Closure lifecycle (RTM Workstream I). Absent on a freshly-raised gap; set
   *  when a caller inserts an already-closed finding (e.g. an override cascade
   *  that closes a line that had no prior row). `firstRaisedAt` is NOT accepted —
   *  the schema defaults it to the insert time. */
  closedAt?: string;
  closedVia?: string;
}

/**
 * An incremental finding edit. Historically only the six reviewer-facing columns;
 * WIDENED for RTM Workstream I so `reconcileFindingsForTx` can update a matched
 * finding IN PLACE with the full re-captured state (never delete-then-reinsert) —
 * the same fields {@link findingInsertVars} writes, minus the identity columns
 * (`transactionId`/`referenceNo`/`criterion`) and the immutable `firstRaisedAt`.
 * Every field stays optional and only present keys are emitted (siblings on the
 * row are never clobbered), so the incremental single-finding editor is unaffected.
 */
export interface FindingUpdateInput {
  text?: string;
  source?: string;
  legacyStatus?: string;
  anchors?: string[];
  suggestedCompliance?: string;
  finalCompliance?: string;
  rationale?: string;
  confidence?: string;
  route?: string;
  matchedRules?: string[];
  effectiveStatus?: string;
  reviewNotes?: string;
  regulatoryRefs?: RuleReferences;
  aiPfmaSection?: string;
  aiCitations?: string;
  aiModel?: string;
  aiRunId?: string;
  /** Closure lifecycle. Pass `null` to CLEAR (a matched finding that re-opened);
   *  a value to close it. Omit to leave unchanged. */
  closedAt?: string | null;
  closedVia?: string | null;
}

// --- Coercion helpers ------------------------------------------------------

/**
 * Normalise an email for a write: blank/absent collapses to `null` (the SQL
 * column is nullable, and `null` is the unambiguous "unset" the mutation wants —
 * never an empty string masquerading as a value).
 */
function emailVar(v: string | null | undefined): string | null {
  const e = normalizeEmail(v);
  return e === '' ? null : e;
}

/** Coerce a possibly-stringy value to a finite number; non-numeric → null. */
function numVar(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** A scalar text value for a write: blank/absent collapses to `null` (unset). */
function textVar(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = v.trim();
  return t === '' ? null : t;
}

/**
 * Serialise a finding's regulatory references to a JSON string for storage.
 * Absent, or an object with no non-empty lists, collapses to `null` (unset) so
 * the column stays clean rather than holding an empty `{}`.
 */
function serialiseRefs(refs: RuleReferences | null | undefined): string | null {
  if (!refs) return null;
  const hasAny =
    (refs.eskomPolicy?.length ?? 0) > 0 ||
    (refs.pfma?.length ?? 0) > 0 ||
    (refs.law?.length ?? 0) > 0;
  return hasAny ? JSON.stringify(refs) : null;
}

/** Assign `value` to `vars[key]` only when it is not undefined (keeps inserts tidy). */
function setIf<T extends object, K extends keyof T>(vars: T, key: K, value: T[K] | undefined): void {
  if (value !== undefined) vars[key] = value;
}

// --- Pure variable mappers -------------------------------------------------

/**
 * Map a clean {@link TransactionInsertInput} → InsertTransactionDataVariables.
 * `value` is coerced to a number; emails are normalised; absent optional fields
 * are omitted so the insert carries only what was supplied.
 */
export function transactionInsertVars(input: TransactionInsertInput): InsertTransactionDataVariables {
  const vars: InsertTransactionDataVariables = { referenceNo: input.referenceNo };
  setIf(vars, 'baseNo', input.baseNo);
  setIf(vars, 'recordLabel', input.recordLabel);
  setIf(vars, 'dataset', input.dataset);
  setIf(vars, 'year', input.year);
  setIf(vars, 'period', input.period);
  setIf(vars, 'division', input.division);
  setIf(vars, 'supplier', input.supplier);
  setIf(vars, 'csdNumber', input.csdNumber);
  if (input.value !== undefined) vars.value = numVar(input.value);
  setIf(vars, 'valueDisplay', input.valueDisplay);
  setIf(vars, 'mechanism', input.mechanism);
  setIf(vars, 'gapsStatus', input.gapsStatus);
  setIf(vars, 'complianceLevel', input.complianceLevel);
  setIf(vars, 'complianceLevelOverride', input.complianceLevelOverride);
  setIf(vars, 'selfDeclaration', input.selfDeclaration);
  setIf(vars, 'initialFindings', input.initialFindings);
  setIf(vars, 'updatedFindings', input.updatedFindings);
  setIf(vars, 'findingText', input.findingText);
  setIf(vars, 'link', input.link);
  setIf(vars, 'reviewStatus', input.reviewStatus);
  setIf(vars, 'progress', input.progress);
  setIf(vars, 'workingPaperNotes', input.workingPaperNotes);
  if (input.assignedToEmail !== undefined) vars.assignedToEmail = emailVar(input.assignedToEmail);
  if (input.assignedByEmail !== undefined) vars.assignedByEmail = emailVar(input.assignedByEmail);
  if (input.reviewerEmail !== undefined) vars.reviewerEmail = emailVar(input.reviewerEmail);
  setIf(vars, 'reviewed', input.reviewed);
  // reviewedAt/capturedDate are UPDATE-path only — a brand-new transaction is not
  // yet reviewed or captured (the InsertTransactionData mutation has no such column).
  setIf(vars, 'onDashboard', input.onDashboard);
  setIf(vars, 'panel', input.panel);
  setIf(vars, 'assignedAt', input.assignedAt);
  setIf(vars, 'ou', input.ou);
  setIf(vars, 'contractNumber', input.contractNumber);
  setIf(vars, 'cirNumber', input.cirNumber);
  setIf(vars, 'linkProvided', input.linkProvided);
  setIf(vars, 'linkIssue', input.linkIssue);
  // R3 #1: csdNumber is reviewer-entered from OpenText, so it IS updatable.
  setIf(vars, 'csdNumber', input.csdNumber);
  setIf(vars, 'bu', input.bu);
  setIf(vars, 'briefDescription', input.briefDescription);
  setIf(vars, 'buyer', input.buyer);
  setIf(vars, 'prNumber', input.prNumber);
  setIf(vars, 'procurementManager', input.procurementManager);
  setIf(vars, 'endUser', input.endUser);

  return vars;
}

/**
 * Map a clean {@link TransactionUpdateInput} + a REAL transaction `id` →
 * UpdateTransactionDataVariables. Never emits `referenceNo` or `rowVersion`
 * (neither is a writable update column — see the type note above).
 */
export function transactionUpdateVars(id: string, input: TransactionUpdateInput): UpdateTransactionDataVariables {
  const vars: UpdateTransactionDataVariables = { id };
  setIf(vars, 'dataset', input.dataset);
  setIf(vars, 'period', input.period);
  setIf(vars, 'division', input.division);
  setIf(vars, 'supplier', input.supplier);
  if (input.value !== undefined) vars.value = numVar(input.value);
  setIf(vars, 'valueDisplay', input.valueDisplay);
  setIf(vars, 'mechanism', input.mechanism);
  // `reviewed` is the persisted boolean review flag (update-only column). Only
  // emit it when the caller supplied one, so an unrelated update never clears it.
  setIf(vars, 'reviewed', input.reviewed);
  setIf(vars, 'reviewedAt', input.reviewedAt);
  setIf(vars, 'capturedDate', input.capturedDate);
  setIf(vars, 'gapsStatus', input.gapsStatus);
  setIf(vars, 'complianceLevel', input.complianceLevel);
  setIf(vars, 'complianceLevelOverride', input.complianceLevelOverride);
  setIf(vars, 'selfDeclaration', input.selfDeclaration);
  setIf(vars, 'initialFindings', input.initialFindings);
  setIf(vars, 'updatedFindings', input.updatedFindings);
  setIf(vars, 'findingText', input.findingText);
  setIf(vars, 'link', input.link);
  setIf(vars, 'reviewStatus', input.reviewStatus);
  setIf(vars, 'progress', input.progress);
  setIf(vars, 'workingPaperNotes', input.workingPaperNotes);
  if (input.assignedToEmail !== undefined) vars.assignedToEmail = emailVar(input.assignedToEmail);
  if (input.assignedByEmail !== undefined) vars.assignedByEmail = emailVar(input.assignedByEmail);
  if (input.reviewerEmail !== undefined) vars.reviewerEmail = emailVar(input.reviewerEmail);
  setIf(vars, 'onDashboard', input.onDashboard);
  setIf(vars, 'panel', input.panel);
  setIf(vars, 'assignedAt', input.assignedAt);
  setIf(vars, 'ou', input.ou);
  setIf(vars, 'contractNumber', input.contractNumber);
  setIf(vars, 'cirNumber', input.cirNumber);
  setIf(vars, 'linkProvided', input.linkProvided);
  setIf(vars, 'linkIssue', input.linkIssue);
  // R3 #1: csdNumber is reviewer-entered from OpenText, so it IS updatable.
  setIf(vars, 'csdNumber', input.csdNumber);
  setIf(vars, 'bu', input.bu);
  setIf(vars, 'briefDescription', input.briefDescription);
  setIf(vars, 'buyer', input.buyer);
  setIf(vars, 'prNumber', input.prNumber);
  setIf(vars, 'procurementManager', input.procurementManager);
  setIf(vars, 'endUser', input.endUser);

  return vars;
}


/**
 * Map a {@link TransactionUpdateInput} + a REAL transaction `id` → import-scoped
 * UpdateTransactionDataVariables: {@link IMPORT_UPDATE_COLUMNS} only. The bulk
 * import's update path uses THIS mapper instead of {@link transactionUpdateVars}
 * so a re-imported file can never overwrite review/work-product/assignment state
 * on a live row, whatever columns the spreadsheet happens to carry. Same setIf
 * semantics: an absent field is omitted and therefore left unchanged.
 */
export function transactionImportUpdateVars(
  id: string,
  input: TransactionUpdateInput,
): UpdateTransactionDataVariables {
  // Derived from the FULL mapper, then narrowed to the whitelist: every
  // column's coercion and omission rule (numVar for value, setIf absence, any
  // future fold) is inherited by construction, so the two mappers can never
  // silently diverge — the whitelist stays the single thing to review.
  const full = transactionUpdateVars(id, input) as unknown as Record<string, unknown>;
  const vars: Record<string, unknown> = { id };
  for (const col of IMPORT_UPDATE_COLUMNS) {
    if (col in full) vars[col] = full[col];
  }
  return vars as unknown as UpdateTransactionDataVariables;
}

/**
 * Map a {@link TransactionUpdateInput} + a REAL transaction `id` → bulk-update-scoped
 * UpdateTransactionDataVariables: {@link BULK_UPDATE_COLUMNS} only. Same
 * derive-from-the-full-mapper-then-narrow construction as
 * {@link transactionImportUpdateVars}, so coercions can never diverge between the
 * three write modes and the whitelist stays the one thing to review.
 *
 * WIDER than the import mapper by design (Lindiwe R6 — bulk Excel does what a
 * manual edit does). The narrowing that matters is upstream: the parser emits
 * ONLY the columns the workbook actually carries, so an absent column never
 * reaches here and the row keeps its value.
 */
export function transactionBulkUpdateVars(
  id: string,
  input: TransactionUpdateInput,
): UpdateTransactionDataVariables {
  const full = transactionUpdateVars(id, input) as unknown as Record<string, unknown>;
  const vars: Record<string, unknown> = { id };
  for (const col of BULK_UPDATE_COLUMNS) {
    if (col in full) vars[col] = full[col];
  }
  return vars as unknown as UpdateTransactionDataVariables;
}

/**
 * Map a {@link WorkingPaperInput} + a REAL transaction `id` →
 * InsertWorkingPaperVariables. Binds the paper to its transaction; emails
 * normalised.
 */
export function workingPaperInsertVars(transactionId: string, input: WorkingPaperInput): InsertWorkingPaperVariables {
  const vars: InsertWorkingPaperVariables = { transactionId };
  setIf(vars, 'progress', input.progress);
  setIf(vars, 'complianceLevel', input.complianceLevel);
  setIf(vars, 'findingText', input.findingText);
  setIf(vars, 'workingPaperNotes', input.workingPaperNotes);
  setIf(vars, 'reviewStatus', input.reviewStatus);
  if (input.reviewerEmail !== undefined) vars.reviewerEmail = emailVar(input.reviewerEmail);
  setIf(vars, 'reviewerName', input.reviewerName);
  setIf(vars, 'reviewedAt', input.reviewedAt);
  setIf(vars, 'capturedDate', input.capturedDate);
  if (input.assignedToEmail !== undefined) vars.assignedToEmail = emailVar(input.assignedToEmail);
  if (input.assignedByEmail !== undefined) vars.assignedByEmail = emailVar(input.assignedByEmail);
  if (input.capturedByEmail !== undefined) vars.capturedByEmail = emailVar(input.capturedByEmail);
  return vars;
}

/**
 * Map a {@link WorkingPaperInput} + a REAL working-paper `id` →
 * UpdateWorkingPaperVariables. Keyed by the paper's own UUID; never emits
 * `transactionId` (not an update column).
 */
export function workingPaperUpdateVars(id: string, input: WorkingPaperInput): UpdateWorkingPaperVariables {
  const vars: UpdateWorkingPaperVariables = { id };
  setIf(vars, 'progress', input.progress);
  setIf(vars, 'complianceLevel', input.complianceLevel);
  setIf(vars, 'findingText', input.findingText);
  setIf(vars, 'workingPaperNotes', input.workingPaperNotes);
  setIf(vars, 'reviewStatus', input.reviewStatus);
  if (input.reviewerEmail !== undefined) vars.reviewerEmail = emailVar(input.reviewerEmail);
  setIf(vars, 'reviewerName', input.reviewerName);
  setIf(vars, 'reviewedAt', input.reviewedAt);
  setIf(vars, 'capturedDate', input.capturedDate);
  if (input.assignedToEmail !== undefined) vars.assignedToEmail = emailVar(input.assignedToEmail);
  if (input.assignedByEmail !== undefined) vars.assignedByEmail = emailVar(input.assignedByEmail);
  if (input.capturedByEmail !== undefined) vars.capturedByEmail = emailVar(input.capturedByEmail);
  return vars;
}

/**
 * Map a {@link WorkingPaperInput} + a REAL transaction `id` →
 * UpdateWorkingPaperByTxVariables. Keyed on the transaction FK (the @unique
 * `working_paper_transactionId_uidx`) rather than the paper's own UUID, so the
 * update lands WITHOUT first reading the paper — the basis of the read-free upsert
 * in {@link upsertWorkingPaperForTx}. Same writable field set as
 * {@link workingPaperUpdateVars}; never emits `transactionId` as a data column (it
 * is the WHERE key, not a writable field).
 */
export function workingPaperUpdateByTxVars(
  transactionId: string,
  input: WorkingPaperInput,
): UpdateWorkingPaperByTxVariables {
  const vars: UpdateWorkingPaperByTxVariables = { transactionId };
  setIf(vars, 'progress', input.progress);
  setIf(vars, 'complianceLevel', input.complianceLevel);
  setIf(vars, 'findingText', input.findingText);
  setIf(vars, 'workingPaperNotes', input.workingPaperNotes);
  setIf(vars, 'reviewStatus', input.reviewStatus);
  if (input.reviewerEmail !== undefined) vars.reviewerEmail = emailVar(input.reviewerEmail);
  setIf(vars, 'reviewerName', input.reviewerName);
  setIf(vars, 'reviewedAt', input.reviewedAt);
  setIf(vars, 'capturedDate', input.capturedDate);
  if (input.assignedToEmail !== undefined) vars.assignedToEmail = emailVar(input.assignedToEmail);
  if (input.assignedByEmail !== undefined) vars.assignedByEmail = emailVar(input.assignedByEmail);
  if (input.capturedByEmail !== undefined) vars.capturedByEmail = emailVar(input.capturedByEmail);
  return vars;
}

/**
 * Map a {@link CesRowInput} + REAL working-paper & transaction ids →
 * InsertCesRowVariables. `seq` coerced to number; optional text columns default
 * to `null` (unset). An empty `status` string collapses to `null`.
 */
export function cesRowVars(workingPaperId: string, transactionId: string, row: CesRowInput): InsertCesRowVariables {
  return {
    workingPaperId,
    transactionId,
    seq: numVar(row.seq) ?? 0,
    stage: row.stage,
    criterion: row.criterion,
    status: textVar(row.status),
    finding: textVar(row.finding),
  };
}

/**
 * Map a {@link FindingInsertInput} + a REAL transaction `id` and the join
 * `referenceNo` → InsertFindingRowVariables. Arrays default to `[]`; scalar
 * columns default to `null`; `seeded` defaults to false.
 */
export function findingInsertVars(
  transactionId: string,
  referenceNo: string | null,
  f: FindingInsertInput,
): InsertFindingRowVariables {
  return {
    transactionId,
    referenceNo: referenceNo ?? null,
    text: f.text,
    source: textVar(f.source),
    legacyStatus: textVar(f.legacyStatus),
    anchors: f.anchors ?? [],
    suggestedCompliance: textVar(f.suggestedCompliance),
    finalCompliance: textVar(f.finalCompliance),
    rationale: textVar(f.rationale),
    confidence: textVar(f.confidence),
    route: textVar(f.route),
    matchedRules: f.matchedRules ?? [],
    effectiveStatus: textVar(f.effectiveStatus),
    reviewNotes: textVar(f.reviewNotes),
    regulatoryRefs: serialiseRefs(f.regulatoryRefs),
    criterion: textVar(f.criterion),
    aiPfmaSection: textVar(f.aiPfmaSection),
    aiCitations: textVar(f.aiCitations),
    aiModel: textVar(f.aiModel),
    aiRunId: textVar(f.aiRunId),
    seeded: f.seeded === true,
    // RTM Workstream I: a fresh gap has no closure; firstRaisedAt rides the schema
    // default (request.time). closedAt/closedVia only carry when a caller inserts an
    // already-closed finding (e.g. an override cascade line with no prior row).
    closedAt: textVar(f.closedAt),
    closedVia: textVar(f.closedVia),
  };
}

/**
 * Map an incremental {@link FindingUpdateInput} + the finding's UUID →
 * UpdateFindingRowVariables. Only the columns PRESENT in the patch are emitted, so
 * siblings on the row are never clobbered (an omitted field is left unchanged); an
 * explicit `null` IS emitted and clears the column (reconcile re-opening a finding:
 * closedAt/closedVia → null). Widened for RTM Workstream I so reconcile can refresh
 * a matched finding in full IN PLACE — the same columns an insert writes, minus the
 * identity (`criterion`) and immutable `firstRaisedAt`.
 */
export function findingUpdateVars(id: string, patch: FindingUpdateInput): UpdateFindingRowVariables {
  const vars: UpdateFindingRowVariables = { id };
  setIf(vars, 'text', patch.text);
  setIf(vars, 'source', patch.source);
  setIf(vars, 'legacyStatus', patch.legacyStatus);
  setIf(vars, 'anchors', patch.anchors);
  setIf(vars, 'suggestedCompliance', patch.suggestedCompliance);
  setIf(vars, 'finalCompliance', patch.finalCompliance);
  setIf(vars, 'rationale', patch.rationale);
  setIf(vars, 'confidence', patch.confidence);
  setIf(vars, 'route', patch.route);
  setIf(vars, 'matchedRules', patch.matchedRules);
  setIf(vars, 'effectiveStatus', patch.effectiveStatus);
  setIf(vars, 'reviewNotes', patch.reviewNotes);
  // regulatoryRefs is JSON-serialised (same as the insert mapper) when present.
  if (patch.regulatoryRefs !== undefined) vars.regulatoryRefs = serialiseRefs(patch.regulatoryRefs);
  setIf(vars, 'aiPfmaSection', patch.aiPfmaSection);
  setIf(vars, 'aiCitations', patch.aiCitations);
  setIf(vars, 'aiModel', patch.aiModel);
  setIf(vars, 'aiRunId', patch.aiRunId);
  // Closure lifecycle — null CLEARS (emitted), undefined leaves unchanged (skipped).
  setIf(vars, 'closedAt', patch.closedAt);
  setIf(vars, 'closedVia', patch.closedVia);
  return vars;
}

// --- Firebase-bound wrappers -----------------------------------------------

/**
 * Resolve a transaction `referenceNo` to its REAL `{ id, rowVersion }`, throwing
 * a descriptive error when it does not exist. The shared precondition for every
 * write that targets an existing transaction.
 */
async function requireTxId(referenceNo: string): Promise<{ id: string; rowVersion: number }> {
  const tx = await resolveTransactionRef(referenceNo);
  if (!tx) throw new Error(`No transaction found for reference "${referenceNo}".`);
  return tx;
}

/**
 * Resolve a transaction's REAL id from its @unique `referenceNo` via a WRITE
 * (`TransactionIdByRef` → UPDATE … RETURNING id), so it never depends on a SELECT that can
 * lag behind a just-committed row. Returns the id, or `null` when no row matches. This is
 * the id-yielding counterpart to {@link upsertWorkingPaperForTx}'s update-first recovery:
 * the generated `transaction_updateMany` returns only a count, so a non-primary-key write
 * can surface the row's id only through a RETURNING clause (see TransactionIdByRef in
 * mutations.gql). Used by {@link upsertTransaction}'s read-free race recovery.
 */
async function resolveTransactionIdByWrite(referenceNo: string): Promise<string | null> {
  const { data } = await transactionIdByRef(dc, { referenceNo });
  const id = (data.row as { id?: string } | null | undefined)?.id;
  return id ?? null;
}

// --- Audit instrumentation -------------------------------------------------
// Every domain wrapper below records ONE append-only AuditLog entry after its
// primary write succeeds, so the trail captures "every change and by whom". The
// actor is stamped SERVER-SIDE — the AuditLog.actorUid/actorEmail column defaults
// read the VERIFIED auth token — and is never sent from the client, so "by whom"
// cannot be forged (see dataconnect/schema/schema.gql → AuditLog).
//
// Bulk / composite flows (population import, CES capture, allocation) would emit a
// flood of per-row entries; they wrap their inner writes in `withAuditSuppressed`
// and record a single summary entry instead. Suppression is a save/restore
// boolean — safe because these flows are serial, user-initiated actions on a
// single-threaded event loop (no unrelated mutation interleaves mid-flow).
let auditSuppressed = false;

/**
 * Run `fn` with the per-wrapper audit writes suppressed, then restore the prior
 * state. Bulk/composite callers use this and record ONE summary entry of their
 * own (e.g. `import_population`, `ces_capture`) rather than one row per sub-write.
 */
export async function withAuditSuppressed<T>(fn: () => Promise<T>): Promise<T> {
  const prev = auditSuppressed;
  auditSuppressed = true;
  try {
    return await fn();
  } finally {
    auditSuppressed = prev;
  }
}

/**
 * Append an AuditLog entry, BEST-EFFORT: honours `withAuditSuppressed` and never
 * throws (a failed audit only `console.warn`s — it must not break the primary
 * write). The actor columns are NOT passed here; the schema defaults stamp them
 * from the verified token. This is the single audit writer every wrapper + hook
 * funnels through.
 */
export async function recordAudit(input: AuditLogInput): Promise<void> {
  if (auditSuppressed) return;
  try {
    await insertAuditLogMutation(dc, auditLogVars(input));
  } catch (err) {
    console.warn(`Audit write failed for ${input.action} (non-blocking):`, err);
  }
}

/**
 * Fresh-cycle review reset, overlaid on the import payload when a transaction is REVIVED
 * from a "replace population" purge (see {@link upsertTransaction} `opts.revive`). Definite
 * NON-NULL values on purpose: Data Connect's `_update` treats a null variable as "leave
 * unchanged" — the very reason the field-locked {@link transactionUpdateVars} never cleared
 * these on re-import — so the only way to overwrite stale review state is a concrete value.
 * (`reviewerEmail`/`reviewedAt` are left as-is: they are inert once `reviewStatus` is no
 * longer "Reviewed" — every reviewed-count surface filters on that — and DC cannot null them.)
 */
const REVIVE_REVIEW_RESET = { reviewed: false, reviewStatus: 'Not Reviewed' } as const;

/**
 * Clear the stale "Reviewed" status on the working paper carried forward by a revived
 * transaction, so the WP register/exports (which read `wp.reviewStatus`) agree with the
 * reset transaction. ONLY the status is touched — the paper's notes/findings carry forward
 * (the useful part of the carry-forward) for the reviewer's fresh pass. A no-op when the
 * transaction has no paper (`workingPaper_updateMany` matches zero rows by `transactionId`).
 */
async function resetRevivedWorkingPaperReview(transactionId: string): Promise<void> {
  await updateWorkingPaperByTx(
    dc,
    workingPaperUpdateByTxVars(transactionId, { reviewStatus: REVIVE_REVIEW_RESET.reviewStatus }),
  );
}

/**
 * Mirror a transaction's review status onto its working paper, keyed by the transaction
 * id. Used by the Transaction editor so a review change made from the register also
 * reaches `WorkingPaper.reviewStatus` — the value the Working Papers / Findings /
 * Allocation surfaces read first. A NO-OP when the transaction has no paper
 * (`workingPaper_updateMany` matches zero rows by `transactionId`), so it never creates
 * an empty paper. Notes/findings are left to the Working Paper editor.
 *
 * `complianceLevel` (29 Sep 2026, Lindiwe: "it doesn't update the compliance status of a
 * transaction"): when the Transaction editor sets a Compliance override, the SAME level is
 * mirrored onto the paper — the value the Working Papers list and its filter read first —
 * so a verdict given from the register is not contradicted by a stale paper. Omitted (a
 * cleared override) leaves the paper's level alone.
 */
export async function syncWorkingPaperReviewByTx(
  transactionId: string,
  reviewStatus: string,
  complianceLevel?: string,
): Promise<void> {
  assertWritable();
  // Mirror the review DATE onto the paper too when it is being marked Reviewed, so
  // the working-paper document and the register agree (owner feedback #8, Aug 2026).
  const reviewedAt =
    reviewStatus.trim().toLowerCase() === 'reviewed' ? new Date().toISOString() : undefined;
  await updateWorkingPaperByTx(
    dc,
    workingPaperUpdateByTxVars(transactionId, { reviewStatus, reviewedAt, complianceLevel }),
  );
}

/**
 * Insert-or-update a transaction by `referenceNo`. If the reference already
 * resolves, update by the REAL id (writable fields only); otherwise insert.
 * Returns the resulting `{ id, referenceNo }`.
 *
 * `opts.revive` (bulk import only): after a "replace population" purge, every prior
 * reference still exists as a SOFT-DELETED row holding the @unique referenceNo, so a
 * re-import resolves it and lands as an UPDATE — but the field-locked update never
 * clears `deleted`, leaving the row hidden. With `revive`, un-delete it so the
 * re-uploaded population reappears. A no-op on an already-live row. NOT set for ordinary
 * edits, so a reviewer's save can never resurrect a deliberately purged transaction.
 *
 * A revive also RESETS the review lifecycle ({@link REVIVE_REVIEW_RESET}): the prior
 * review belonged to the REMOVED population, so the re-import starts a fresh cycle. Without
 * it the carried-forward `reviewed`/`reviewStatus` made the dashboard "Reviewed" count
 * reference reviews from a purged-then-reimported population (Jun 25 2026). The reset keys
 * off the resolved row's ACTUAL `deleted` state — never the call-level flag — so an
 * ordinary re-upload touching LIVE transactions keeps their reviews (the deliberate
 * corrected-spreadsheet carry-forward; before WS2 the flag alone decided, and every
 * changed live row of a re-import lost its review — the 1 Jul 15:15 clobber).
 *
 * `opts.importSource` (bulk import only, WS2 — 1 Jul 2026 incident): the update lands
 * through {@link transactionImportUpdateVars} (source columns only) instead of the full
 * {@link transactionUpdateVars}, so a re-imported spreadsheet can never overwrite
 * review/work-product/assignment state on an existing row. The revive also switches to
 * ReviveTransactionForImport (conditional native SQL): un-delete AND detach the row plus
 * its soft-deleted findings from their removal batch in one write, so a later
 * batch-restore cannot double-apply to a row that already came back.
 */
export async function upsertTransaction(
  input: TransactionInsertInput,
  opts: { revive?: boolean; importSource?: boolean; audit?: AuditContext } = {},
): Promise<{ id: string; referenceNo: string; action: 'inserted' | 'updated' }> {
  assertWritable();
  // The audit note + from/to payload the caller diffed (Manage / working-paper
  // editors). Recorded on every write path below so a transaction's timeline
  // shows WHAT changed. No payload when nothing labelled moved — a no-op save
  // stays a plain stamp, exactly as before this change.
  const auditNote = opts.audit?.note;
  const auditPayload = opts.audit?.changes?.length
    ? { source: 'transaction', changes: opts.audit.changes }
    : undefined;
  // Stamp the review date when a review is being COMPLETED (the `reviewed` flag is
  // set true), unless the caller already supplied one. Owner feedback #8 (Aug 2026:
  // "date stamps indicating when transactions were reviewed"). Manage's base-field
  // edits never set `reviewed`, and import updates go through the source-only mapper
  // that strips it, so neither moves the date — only a genuine review does.
  if (input.reviewed === true && input.reviewedAt === undefined) {
    input = { ...input, reviewedAt: new Date().toISOString() };
  }
  const existing = await resolveTransactionRef(input.referenceNo);
  // A revive happens ONLY when the resolved row is genuinely SOFT-DELETED. The
  // `revive` option is call-level (the import sets it for the whole run), so by
  // itself it must never reset a LIVE row's review state.
  const isRevive = Boolean(opts.revive && existing?.deleted);
  const updateVarsFor = (id: string): UpdateTransactionDataVariables => {
    // Both mappers are field-locked to writable update columns, so passing the
    // full input is safe — insert-only keys (referenceNo, baseNo, recordLabel,
    // year) are simply not mapped; the import mapper additionally strips
    // review/work-product/assignment columns (and `period`, which only the
    // manual editors and the bulk update may move).
    const vars = opts.importSource
      ? transactionImportUpdateVars(id, input)
      : transactionUpdateVars(id, input);
    // The fresh-cycle reset rides the VARS (definite non-null values — DC leaves
    // an absent variable unchanged, and the import mapper drops review columns
    // from the input), applied once for both mapper modes.
    if (isRevive) Object.assign(vars, REVIVE_REVIEW_RESET);
    return vars;
  };
  // Un-delete the resolved row + reset its carried-forward working paper. The
  // import path first detaches the row's soft-deleted findings from their removal
  // batch (while the parent is still deleted, so its subquery can resolve the id),
  // THEN un-deletes the transaction — two single-statement native-SQL ops keyed on
  // the @unique referenceNo (they cannot be one data-modifying CTE; see
  // DetachRevivedFindings / ReviveTransactionForImport). Other callers keep the
  // id-keyed connector op.
  const reviveResolved = async (id: string): Promise<void> => {
    if (opts.importSource) {
      await detachRevivedFindings(dc, { referenceNo: input.referenceNo });
      await reviveTransactionForImport(dc, { referenceNo: input.referenceNo });
    } else {
      await reviveTransaction(dc, { id });
    }
    await resetRevivedWorkingPaperReview(id);
  };
  if (existing) {
    await updateTransactionData(dc, updateVarsFor(existing.id));
    if (isRevive) {
      await reviveResolved(existing.id);
    }
    await recordAudit({ action: 'update_transaction', entityType: 'Transaction', entityId: input.referenceNo, note: auditNote, payload: auditPayload });
    return { id: existing.id, referenceNo: input.referenceNo, action: 'updated' };
  }
  try {
    const { data } = await insertTransactionData(dc, transactionInsertVars(input));
    await recordAudit({ action: 'insert_transaction', entityType: 'Transaction', entityId: input.referenceNo, note: auditNote, payload: auditPayload });
    return { id: data.transaction_insert.id, referenceNo: input.referenceNo, action: 'inserted' };
  } catch (err) {
    // Transaction.referenceNo is @unique, and the resolve above + this insert are two
    // separate round-trips — not atomic. Two concurrent NEW-transaction captures for the
    // SAME referenceNo (two admins/tabs, or a read-after-write visibility gap) can both
    // read "not found" and both insert, so the second loses the race with ALREADY_EXISTS.
    // Recover idempotently so the capture still lands (as an update) instead of hard-failing.
    //
    // READ-FREE recovery: the violation PROVES the winner's row exists and is committed on
    // the primary. Resolve its id with a WRITE (resolveTransactionIdByWrite → UPDATE …
    // RETURNING id) keyed on the @unique referenceNo — never a SELECT, which can lag behind
    // the very write that proved the row exists. That lagging re-read was the old flaw here
    // (retryUntilFound(resolveTransactionRef) could exhaust its budget and still surface
    // ALREADY_EXISTS — Lindiwe's intermittent "error during transaction review", Jun 23 2026
    // call), the same class of bug upsertWorkingPaperForTx was rebuilt to remove. A write
    // always hits the authoritative row, so it cannot miss. The id is still returned (it
    // feeds the EvaluationSheetBackup snapshot) and `revive` still composes (by the resolved
    // id) — the two reasons this twin was previously deferred.
    if (!isUniqueViolation(err)) throw err;
    const racedId = await resolveTransactionIdByWrite(input.referenceNo);
    if (!racedId) throw err; // genuinely vanished (RETURNING null) — surface the violation.
    // The raced winner is usually a concurrent fresh INSERT — live, so no revive
    // and no review reset. But the initial resolve can also have MISSED a
    // soft-deleted row on a lagging replica; on the import path the conditional
    // revive op settles it authoritatively (its `WHERE deleted = true …
    // RETURNING` proves whether a revive happened), so it runs FIRST and its
    // answer decides the reset. The classic op cannot prove a revive, so that
    // path un-deletes without resetting (no production caller takes it).
    let revivedInRace = false;
    if (opts.revive) {
      if (opts.importSource) {
        await detachRevivedFindings(dc, { referenceNo: input.referenceNo });
        const r = await reviveTransactionForImport(dc, { referenceNo: input.referenceNo });
        revivedInRace = (r as { data?: { row?: unknown } }).data?.row != null;
      } else {
        await reviveTransaction(dc, { id: racedId });
      }
    }
    const racedVars = opts.importSource
      ? transactionImportUpdateVars(racedId, input)
      : transactionUpdateVars(racedId, input);
    if (revivedInRace) Object.assign(racedVars, REVIVE_REVIEW_RESET);
    await updateTransactionData(dc, racedVars);
    if (revivedInRace) await resetRevivedWorkingPaperReview(racedId);
    await recordAudit({ action: 'update_transaction', entityType: 'Transaction', entityId: input.referenceNo, note: auditNote, payload: auditPayload });
    return { id: racedId, referenceNo: input.referenceNo, action: 'updated' };
  }
}

/**
 * Apply one bulk-Excel update to an EXISTING transaction (Lindiwe R6).
 *
 * Deliberately NOT a mode of {@link upsertTransaction}: the bulk-update screen
 * must never create a transaction and must never revive a purged one. A
 * reference the register does not carry is an operator mistake (wrong file, wrong
 * population) and is reported, not silently inserted — creating rows stays the
 * Import screen's job, where the purge/revive machinery lives.
 *
 * The write is field-locked to {@link BULK_UPDATE_COLUMNS} via
 * {@link transactionBulkUpdateVars}. `note` is the caller's per-row summary of
 * WHICH fields changed — it lands on the audit entry, so a 300-row upload leaves
 * 300 readable lines rather than 300 anonymous "updated" stamps.
 *
 * Throws when the reference resolves to nothing or to a soft-deleted row.
 */
export async function bulkUpdateTransaction(
  input: TransactionInsertInput,
  opts: { note?: string; changes?: FieldChange[] } = {},
): Promise<{ id: string; referenceNo: string }> {
  assertWritable();
  const existing = await resolveTransactionRef(input.referenceNo);
  if (!existing) {
    throw new Error(
      `No transaction with reference "${input.referenceNo}" — a bulk update never creates rows. Use Import to add it.`,
    );
  }
  if (existing.deleted) {
    throw new Error(
      `Reference "${input.referenceNo}" was removed from the population. Restore it from the removal history before updating it.`,
    );
  }
  await updateTransactionData(dc, transactionBulkUpdateVars(existing.id, input));
  await recordAudit({
    action: 'bulk_update_transaction',
    entityType: 'Transaction',
    entityId: input.referenceNo,
    note: opts.note,
    // Carry the per-row from/to so bulk's diff survives into the timeline, not
    // just the field labels the note already had.
    payload: opts.changes?.length ? { source: 'bulk', changes: opts.changes } : undefined,
  });
  return { id: existing.id, referenceNo: input.referenceNo };
}

/**
 * Insert-or-update the working paper for a transaction `referenceNo` — UPDATE-FIRST,
 * so the save never depends on a SELECT seeing the paper.
 *
 * The working paper is 1:1 with its transaction (WorkingPaper.transaction is @unique →
 * working_paper_transactionId_uidx). We update IN PLACE by the transaction FK first; if
 * that touches a row, the paper existed and is saved. Only when the update affects ZERO
 * rows do we insert. If a concurrent save wins the insert race (ALREADY_EXISTS on the
 * unique index), we recover with the SAME write — update-by-transactionId — never a
 * read-back.
 *
 * Why update-first rather than the old read-then-insert-or-update: a SELECT can miss a
 * just-committed paper under read-after-write visibility lag (a read replica/snapshot
 * trailing the primary). That made the old path drive an insert which then hard-failed
 * with "violates SQL unique constraint … ALREADY_EXISTS", and its SELECT-based recovery
 * could miss the row too — surfacing the error to the reviewer (Iris's / Lindiwe's
 * recurring working-paper save failure). A write always hits the authoritative row on the
 * primary, so it cannot miss. The paper's id is not returned (no caller consumes it).
 */
export async function upsertWorkingPaperForTx(
  referenceNo: string,
  input: WorkingPaperInput,
): Promise<void> {
  assertWritable();
  // Stamp the capture date (this save IS the capture) and the review date when the
  // paper is being marked Reviewed — unless the caller already supplied either.
  // Owner feedback #8 (Aug 2026: date stamps for when a paper is captured/reviewed).
  const now = new Date().toISOString();
  const reviewed = (input.reviewStatus ?? '').trim().toLowerCase() === 'reviewed';
  input = {
    ...input,
    capturedDate: input.capturedDate ?? now,
    ...(reviewed && input.reviewedAt === undefined ? { reviewedAt: now } : {}),
  };
  const tx = await requireTxId(referenceNo);

  // 1. Update the existing paper in place — one authoritative write, no prior read. A
  //    non-zero rows-affected count means the paper existed and is now saved.
  if (await updateWorkingPaperByTxAffectsRow(tx.id, input)) {
    await recordAudit({ action: 'save_working_paper', entityType: 'WorkingPaper', entityId: referenceNo });
    return;
  }

  // 2. No paper yet — insert one.
  try {
    await insertWorkingPaper(dc, workingPaperInsertVars(tx.id, input));
    await recordAudit({ action: 'save_working_paper', entityType: 'WorkingPaper', entityId: referenceNo });
    return;
  } catch (err) {
    // 3. A concurrent save inserted the paper between our update-probe and this insert, so
    //    ours loses the race on working_paper_transactionId_uidx. The violation PROVES the
    //    paper now exists and is committed on the primary — recover with the SAME write, so
    //    the reviewer's save lands as an update instead of hard-failing. No read-back: a
    //    SELECT could still miss the just-committed row; a write cannot.
    if (!isUniqueViolation(err)) throw err;
    if (await updateWorkingPaperByTxAffectsRow(tx.id, input)) {
      await recordAudit({ action: 'save_working_paper', entityType: 'WorkingPaper', entityId: referenceNo });
      return;
    }
    throw err; // nothing to update — the row vanished; surface the original violation.
  }
}

/**
 * Update the 1:1 working paper for `transactionId` in place (keyed on the @unique
 * transaction FK). Returns true when a row was updated (the paper existed), false when
 * none matched (so the caller must insert). A single authoritative write — never a read.
 */
async function updateWorkingPaperByTxAffectsRow(
  transactionId: string,
  input: WorkingPaperInput,
): Promise<boolean> {
  const { data } = await updateWorkingPaperByTx(dc, workingPaperUpdateByTxVars(transactionId, input));
  return data.workingPaper_updateMany > 0;
}

/**
 * True when a Data Connect mutation rejected because the row already exists / violates
 * a UNIQUE constraint. The server surfaces this as an `ALREADY_EXISTS` GraphQL
 * extension whose message names the SQL unique index; the SDK error may also carry a
 * `code`. Matching on any of those tokens keeps detection resilient to the exact shape.
 */
function isUniqueViolation(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code === 'string' && code.toLowerCase() === 'already-exists') return true;
  const text = err instanceof Error ? err.message : String(err);
  return /ALREADY_EXISTS|unique constraint/i.test(text);
}

/**
 * Replace ALL CES rows for a transaction. Resolves the REAL transaction id and
 * the working-paper id (CES rows belong to a working paper), deletes the existing
 * rows, then inserts the supplied rows in order. A working paper must already
 * exist for the transaction.
 *
 * The working paper is captured in the immediately-preceding step of a CES save
 * (useCaptureCes: upsertWorkingPaperForTx → replaceCesRowsForTx), so on a FIRST-ever
 * capture this lookup can briefly miss it under read-after-write visibility lag — which
 * used to hard-fail with "No working paper found". We POLL the lookup: the paper definitely
 * exists and is not soft-deleted (it was just inserted), so a short backoff reliably covers
 * the lag, while a genuine absence still surfaces the descriptive error after the budget.
 */
export async function replaceCesRowsForTx(referenceNo: string, rows: CesRowInput[]): Promise<void> {
  assertWritable();
  const tx = await requireTxId(referenceNo);
  const wp = await retryUntilFound(async () => {
    const { data } = await workingPaperByTx(dc, { transactionId: tx.id });
    return data.workingPapers[0];
  });
  if (!wp) throw new Error(`No working paper found for reference "${referenceNo}"; capture one before its CES rows.`);
  await deleteCesRowsForTx(dc, { transactionId: tx.id });
  for (const row of rows) {
    await insertCesRow(dc, cesRowVars(wp.id, tx.id, row));
  }
  await recordAudit({
    action: 'replace_ces',
    entityType: 'Transaction',
    entityId: referenceNo,
    note: `${rows.length} row(s)`,
  });
}

/**
 * Summarise a finding set's lifecycle mix for the audit trail (R3 #2 — the
 * gap-closure process must be traceable: who changed what, and the closure
 * progress at each save). Statuses fold through the four-state normaliser;
 * findings with no status count as 'no status'.
 */
export function findingsAuditNote(findings: readonly FindingInsertInput[]): string {
  const head = `${findings.length} finding(s)`;
  if (findings.length === 0) return head;
  const counts = new Map<string, number>();
  for (const f of findings) {
    const status =
      normalizeFindingStatus(f.effectiveStatus ?? f.legacyStatus ?? '') || 'no status';
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  const order = ['Open', 'In Progress', 'Closed', 'Closed by Self-Declaration', 'no status'];
  const parts = [...counts.entries()]
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([status, n]) => `${n} ${status}`);
  return `${head}: ${parts.join(' \u00B7 ')}`;
}

/**
 * RECONCILE a transaction's findings against a fresh capture (RTM Workstream I) —
 * the successor to the old delete-then-reinsert. Resolves the REAL transaction id,
 * reads the current findings, then matches the incoming set by (transaction ×
 * criterion): a criterion still present is UPDATED in place (its id, createdAt and
 * firstRaisedAt preserved), a criterion no longer flagged is KEPT and CLOSED
 * (closedAt + closedVia — a buyer fix recorded, not forgotten), and a new criterion
 * is inserted. Criterion-less "Additional comments" notes carry no lifecycle and are
 * replaced. `opts.closedVia` names the reason for closures THIS reconcile makes
 * because a criterion vanished (default `buyer-fix`; a capture that set a Compliance
 * Override → Compliant passes `override`, a self-declared one `self-declaration`) —
 * incoming findings the caller already marked closed keep their own `closedVia`.
 *
 * `updatedFindings.test`/`dbWriteWrappers.test` lock the plan-application sequence;
 * the pure matching lives in {@link planFindingReconcile}.
 */
export async function reconcileFindingsForTx(
  referenceNo: string,
  findings: FindingInsertInput[],
  opts: { closedVia?: ReconcileClosedVia; now?: string } = {},
): Promise<void> {
  assertWritable();
  const tx = await requireTxId(referenceNo);

  // Read the findings currently on the transaction (the reconcile baseline).
  const { data } = await findingsForTx(dc, { transactionId: tx.id });
  const existing: ReconcileExisting[] = data.findings.map((r) => ({
    id: r.id,
    referenceNo: r.transaction?.referenceNo ?? undefined,
    criterion: r.criterion ?? undefined,
    legacyStatus: r.legacyStatus ?? undefined,
    effectiveStatus: r.effectiveStatus ?? undefined,
    closedAt: r.closedAt ?? undefined,
    closedVia: r.closedVia ?? undefined,
    deleted: false, // the query already filters deleted rows out
  }));

  const now = opts.now ?? new Date().toISOString();
  const plan = planFindingReconcile(existing, findings, { now, closedVia: opts.closedVia });

  // Apply: close/update kept rows in place, insert the new ones, remove replaced
  // notes. A criterion-bearing gap finding is NEVER deleted — only closed.
  for (const { id, patch } of plan.updates) {
    await updateFindingRow(dc, findingUpdateVars(id, patch));
  }
  for (const f of plan.inserts) {
    await insertFindingRow(dc, findingInsertVars(tx.id, referenceNo, f));
  }
  for (const id of plan.deletes) {
    await deleteFinding(dc, { id });
  }

  await recordAudit({
    action: 'reconcile_findings',
    entityType: 'Transaction',
    entityId: referenceNo,
    // The lifecycle mix makes the gap-closure progress readable in the trail.
    note: findingsAuditNote(findings),
  });
}

/**
 * Incrementally update a single finding by its UUID — an edit that must not
 * clobber its siblings (so it does NOT go through the delete-then-reinsert
 * replace path).
 */
export async function updateOneFinding(
  referenceNo: string,
  id: string,
  patch: FindingUpdateInput,
  opts: { changes?: FieldChange[] } = {},
): Promise<void> {
  assertWritable();
  await updateFindingRow(dc, findingUpdateVars(id, patch));
  // Record against the REFERENCE (entityId), not the finding UUID, so a single
  // finding edit joins its transaction's timeline; the finding UUID is kept in
  // the payload. (Recording entityId = the UUID left these edits orphaned from
  // the transaction they belong to.)
  await recordAudit({
    action: 'update_finding',
    entityType: 'Finding',
    entityId: referenceNo,
    payload: opts.changes?.length ? { findingId: id, changes: opts.changes } : { findingId: id },
  });
}

/**
 * Soft-delete a transaction by `referenceNo`. Resolves the REAL transaction id
 * (never a placeholder), then flips its `deleted` flag via SoftDeleteTransaction.
 * Throws a descriptive error when the reference does not resolve. Returns the
 * resolved id (handy for an audit-log entry that wants the surrogate key).
 */
export async function softDeleteTransaction(referenceNo: string): Promise<string> {
  assertWritable();
  const tx = await requireTxId(referenceNo);
  await softDeleteTransactionMutation(dc, { id: tx.id });
  await recordAudit({
    action: 'soft_delete_transaction',
    entityType: 'Transaction',
    entityId: referenceNo,
    payload: { id: tx.id },
  });
  return tx.id;
}

/**
 * Restore a soft-deleted (removed) transaction from the admin Manage screen
 * (Lindiwe R4 feedback #6). Un-deletes the row and resets its review lifecycle
 * (the removed row's review belonged to a prior cycle) — the same semantics as
 * the non-import revive in {@link upsertTransaction}, reused here because
 * {@link REVIVE_REVIEW_RESET} / {@link resetRevivedWorkingPaperReview} are private.
 *
 * `opts.fresh`: also soft-delete the transaction's live findings so the restored
 * record starts clean — the case Lindiwe actually hit (a removed reference still
 * carried its test-review findings). Without it the findings carry forward.
 *
 * Throws when the reference does not resolve or is not actually removed.
 */
export async function restoreTransaction(
  referenceNo: string,
  opts: { fresh?: boolean } = {},
): Promise<string> {
  assertWritable();
  const tx = await resolveTransactionRef(referenceNo);
  if (!tx) throw new Error(`No transaction found for reference "${referenceNo}".`);
  if (!tx.deleted) throw new Error(`Transaction "${referenceNo}" is not removed.`);
  // Fresh: clear the old findings FIRST, while the row is still removed. If this
  // fails the transaction stays removed, so the whole restore is retry-safe — the
  // admin can simply re-run "Restore fresh" (reviving first would leave a live row
  // that still carries its test findings AND can no longer be restored).
  if (opts.fresh) await softDeleteFindingsForTx(dc, { transactionId: tx.id });
  await reviveTransaction(dc, { id: tx.id });
  // Fresh review cycle — definite non-null values (DC leaves an absent variable
  // unchanged), matching the revive path in upsertTransaction.
  await updateTransactionData(dc, { id: tx.id, ...REVIVE_REVIEW_RESET });
  await resetRevivedWorkingPaperReview(tx.id);
  await recordAudit({
    action: opts.fresh ? 'restore_transaction_fresh' : 'restore_transaction',
    entityType: 'Transaction',
    entityId: referenceNo,
    payload: { id: tx.id },
  });
  return tx.id;
}

/** A user's application role + display details, from the UserByEmail DB lookup. */
export interface UserRoleRow {
  role: string;
  displayName: string | null;
  active: boolean;
}

/**
 * Look up a user's role row by email via UserByEmail. Returns the first matching
 * row (`{ role, displayName, active }`) or `null` when no user matches. Emails are
 * normalised to match the stored, lower-cased form. This is the DB tier of
 * `resolveRole`; callers MUST treat a thrown error / null result as "fall back"
 * (never a lock-out) — see src/lib/auth.tsx.
 */
export async function findUserRoleByEmail(email: string): Promise<UserRoleRow | null> {
  const { data } = await userByEmail(dc, { email: normalizeEmail(email) });
  const row = data.users[0];
  if (!row) return null;
  return { role: row.role, displayName: row.displayName ?? null, active: row.active };
}

// --- Best-effort audit / history writes ------------------------------------
// These pure mappers + thin wrappers feed the append-only audit tables. Callers
// wrap them in try/catch so an audit failure NEVER blocks the primary write.

/** An EvaluationSheetBackup snapshot to record (e.g. a CES capture). */
export interface EvaluationSheetBackupInput {
  transactionId: string;
  reviewStatus?: string;
  /** Opaque JSON snapshot of the captured evaluation (rows + findings). */
  fullEvaluation?: unknown;
  /** Opaque JSON snapshot of the working-paper payload. */
  workingPaper?: unknown;
  sourceAction?: string;
}

/** Map an {@link EvaluationSheetBackupInput} → InsertEvaluationSheetBackupVariables. */
export function evaluationSheetBackupVars(
  input: EvaluationSheetBackupInput,
): InsertEvaluationSheetBackupVariables {
  const vars: InsertEvaluationSheetBackupVariables = { transactionId: input.transactionId };
  setIf(vars, 'reviewStatus', textVar(input.reviewStatus) ?? undefined);
  if (input.fullEvaluation !== undefined) vars.fullEvaluation = input.fullEvaluation;
  if (input.workingPaper !== undefined) vars.workingPaper = input.workingPaper;
  setIf(vars, 'sourceAction', textVar(input.sourceAction) ?? undefined);
  return vars;
}

/** An AuditLog entry to append (e.g. a soft-delete). */
export interface AuditLogInput {
  action: string;
  entityType?: string;
  entityId?: string;
  note?: string;
  payload?: unknown;
}

/** Map an {@link AuditLogInput} → InsertAuditLogVariables. */
export function auditLogVars(input: AuditLogInput): InsertAuditLogVariables {
  const vars: InsertAuditLogVariables = { action: input.action };
  setIf(vars, 'entityType', textVar(input.entityType) ?? undefined);
  setIf(vars, 'entityId', textVar(input.entityId) ?? undefined);
  setIf(vars, 'note', textVar(input.note) ?? undefined);
  if (input.payload !== undefined) vars.payload = input.payload;
  return vars;
}

/** A ReportRun audit entry (recorded on a report download). */
export interface ReportRunInput {
  type: string;
  parameters?: unknown;
  format?: string;
  snapshotRef?: string;
  /** Cloud Storage object key of the persisted artifact (reports/{uid}/...), if retained. */
  storagePath?: string;
  summary?: unknown;
  // Report archive (Jun 23 2026 feedback 2c): the server-reserved report number,
  // the as-of date, and the scoped division — `summary` carries the frozen model.
  reportNumber?: string;
  asOfDate?: string;
  division?: string;
}

/** Map a {@link ReportRunInput} → InsertReportRunVariables. */
export function reportRunVars(input: ReportRunInput): InsertReportRunVariables {
  const vars: InsertReportRunVariables = { type: input.type };
  if (input.parameters !== undefined) vars.parameters = input.parameters;
  setIf(vars, 'format', textVar(input.format) ?? undefined);
  setIf(vars, 'snapshotRef', textVar(input.snapshotRef) ?? undefined);
  setIf(vars, 'storagePath', textVar(input.storagePath) ?? undefined);
  if (input.summary !== undefined) vars.summary = input.summary;
  setIf(vars, 'reportNumber', textVar(input.reportNumber) ?? undefined);
  setIf(vars, 'asOfDate', textVar(input.asOfDate) ?? undefined);
  setIf(vars, 'division', textVar(input.division) ?? undefined);
  return vars;
}

/** Insert an EvaluationSheetBackup snapshot. Resolves real ids; callers wrap best-effort. */
export async function insertEvaluationSheetBackup(input: EvaluationSheetBackupInput): Promise<void> {
  await insertEvaluationSheetBackupMutation(dc, evaluationSheetBackupVars(input));
}

/** Append an AuditLog entry. Callers wrap best-effort (never blocks the primary write). */
export async function insertAuditLog(input: AuditLogInput): Promise<void> {
  await insertAuditLogMutation(dc, auditLogVars(input));
}

/** Record a ReportRun. Callers wrap best-effort (never blocks the download). */
export async function insertReportRun(input: ReportRunInput): Promise<void> {
  await insertReportRunMutation(dc, reportRunVars(input));
}

// --- In-app notifications (Jun 23 2026 feedback 4a) ------------------------
// Reading + marking-read are the only CLIENT-facing notification ops; creating a
// notification (for any recipient) is done server-side by admin-api so it cannot
// be forged. Mark-read is self-scoped at the connector (updateMany filtered by the
// verified token email), so a caller can only flip their own. NOT guarded by
// assertWritable — marking one's own notification read is a benign personal action,
// not an edit to the data being previewed, so it still works during impersonation.

/** Mark a single notification (the caller's own) as read. */
export async function markNotificationRead(id: string): Promise<void> {
  await markNotificationReadMutation(dc, { id });
}

/** Mark all of the caller's unread notifications as read. */
export async function markAllNotificationsRead(): Promise<void> {
  await markAllNotificationsReadMutation(dc);
}

// --- RFI Register + RTM sign-off writes (owner feedback point 4, admin-only) ---

export interface RfiInput {
  rfiRef?: string;
  division?: string;
  supplier?: string;
  contractNumber?: string;
  contractValue?: string;
  dateReceivedClient?: string;
  dateReceivedConsultant?: string;
  dateSubmittedDeloitte?: string;
  concerns?: string;
  status?: string;
}

/** Clear the whole RFI register (replace-all import: clear then insert). */
export async function clearRfiRegister(): Promise<void> {
  await clearRfiRegisterMutation(dc);
}

/** Insert one RFI register row. */
export async function insertRfiRow(input: RfiInput): Promise<void> {
  const vars: InsertRfiRegisterVariables = {};
  setIf(vars, 'rfiRef', input.rfiRef);
  setIf(vars, 'division', input.division);
  setIf(vars, 'supplier', input.supplier);
  setIf(vars, 'contractNumber', input.contractNumber);
  setIf(vars, 'contractValue', input.contractValue);
  setIf(vars, 'dateReceivedClient', input.dateReceivedClient);
  setIf(vars, 'dateReceivedConsultant', input.dateReceivedConsultant);
  setIf(vars, 'dateSubmittedDeloitte', input.dateSubmittedDeloitte);
  setIf(vars, 'concerns', input.concerns);
  setIf(vars, 'status', input.status);
  await insertRfiRegisterMutation(dc, vars);
}

export interface RtmInput {
  division: string;
  draftSubmitted: boolean;
  finalSubmitted: boolean;
  signedOff: boolean;
  pendingSignoff: boolean;
  note?: string;
}

/** Upsert one operating unit's RTM sign-off status (keyed by division). */
export async function upsertRtmSignoff(input: RtmInput): Promise<void> {
  const vars: UpsertRtmSignoffVariables = {
    division: input.division,
    draftSubmitted: input.draftSubmitted,
    finalSubmitted: input.finalSubmitted,
    signedOff: input.signedOff,
    pendingSignoff: input.pendingSignoff,
    updatedAt: new Date().toISOString(),
  };
  setIf(vars, 'note', input.note);
  await upsertRtmSignoffMutation(dc, vars);
}


// ---- Wednesday Composer: authored narratives + RTM status (Phase 1b) --------
export interface ReportNarrativeInput {
  id: string;
  fy: string;
  scopeKey: string;
  slideKey: string;
  division: string;
  text: string;
  draftInputs?: Record<string, number> | null;
  edited: boolean;
}
export interface RtmStatusInput {
  id: string;
  fy: string;
  quarter: string;
  division: string;
  submitted?: string | null;
  signedOff?: string | null;
  submittedDate?: string | null;
  signedOffDate?: string | null;
  note?: string | null;
  rtmReportKey?: string | null;
}

/** Upsert an edited deck narrative (admin-only; updatedBy/updatedAt server-stamped). */
export async function upsertReportNarrative(input: ReportNarrativeInput): Promise<void> {
  assertWritable();
  await upsertReportNarrativeMutation(dc, {
    id: input.id,
    fy: input.fy,
    scopeKey: input.scopeKey,
    slideKey: input.slideKey,
    division: input.division,
    text: input.text,
    draftInputs: input.draftInputs ?? null,
    edited: input.edited,
  });
}

/** Delete a narrative override (reset to auto-draft). */
export async function deleteReportNarrative(id: string): Promise<void> {
  assertWritable();
  await deleteReportNarrativeMutation(dc, { id });
}

/** Upsert a reports-to-management status cell set. */
export async function upsertRtmStatus(input: RtmStatusInput): Promise<void> {
  assertWritable();
  await upsertRtmStatusMutation(dc, {
    id: input.id,
    fy: input.fy,
    quarter: input.quarter,
    division: input.division,
    submitted: input.submitted ?? null,
    signedOff: input.signedOff ?? null,
    submittedDate: input.submittedDate ?? null,
    signedOffDate: input.signedOffDate ?? null,
    note: input.note ?? null,
    rtmReportKey: input.rtmReportKey ?? null,
  });
}
